import { Button, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFetch } from 'use-http';
import graph3 from 'assets/icons/graphs/graph3.svg';
import { DrawerNewInspection, InfoBox } from 'components';
import { InspectionStatus } from 'constants/inspections';
import { STRUCTURES_PATHS } from 'constants/paths';
import { PermissionType } from 'constants/permissions';
import { useSubscription } from 'hooks';
import usePromises from 'hooks/usePromises';
import {
  flattenOverviewL3,
  L3RecapContent,
} from 'pages/Inspections/CreateInspection/L3/L3RecapContent';
import { useGlobalSelector } from 'stores';
import { inspectionTabMapping } from 'utils/inspectionTabMapping';
import { hasL3ModuleAccess } from 'utils/l3Access';
import { handleDynamicInspectionsTabChange } from 'utils/utilities';

// Tab "Idraulica" del Dettaglio Ponte — gemella di TabL0.jsx/TabL1.jsx,
// stesso pattern di fetch (GET bridges/:id/overview/L3 + overviewImages
// ?level=L3, apps/api/plans/features/task-21-l3-riepilogo-ponte.md).
// Contenuto/layout condivisi con L3Recap.jsx (sotto-tab "Riepilogo" del
// form attività) tramite L3RecapContent — stessa fonte dati (overview/L3)
// per entrambi, vedi commento in testa a L3RecapContent.jsx.
const TabL3 = ({
  structure,
  userPermissionOnStructure,
  openDrawerNewInspection,
  setOpenDrawerNewInspection,
}) => {
  const { user } = useSubscription();
  const { setPromises } = usePromises();
  const params = useParams();
  const id = params.structureId;
  const inspectionId = params?.inspectionId;
  const navigate = useNavigate();
  const { get } = useFetch();
  const { showLoader, hideLoader } = useGlobalSelector();
  const [overviewL3, setOverviewL3] = useState();
  const [imagesList, setImagesList] = useState();

  const isOwner =
    structure?.ownerId === user?.id || structure?.ownerId === user?.parent?.id;

  // e-2026-06-t21 — id di navigazione L3 (dal dettaglio ponte, non da
  // overviewL3): il bottone "Approfondisci" deve aprire l'ultima ATTIVITÀ L3,
  // non l'ultima ispezione di qualunque tipo. Va incrociato con
  // l3Access.canView (proprietà del proprietario del ponte): se falso
  // l'apertura risponderebbe 403 anche con l'id valorizzato. Vedi
  // apps/api/docs/navigazione-ultima-attivita-per-livello.md.
  const lastL3InspectionId = structure?.requestUserLastValidL3InspectionId;
  const canOpenLastL3Inspection =
    !!lastL3InspectionId && !!structure?.l3Access?.canView;

  // Task 18, aggiornamento 2026-09-01 — ⚠️ sul DTO di PONTE `l3Access.canEdit`
  // significa "posso creare una nuova attività L3 qui" (OR proprietario
  // ponte/utente), NON "posso modificare l'attività L3 esistente" (che è OR
  // a TRE: + proprietario dell'attività, disponibile solo sul DTO di
  // attività). Per questo banner (riepilogo, non l'attività aperta) il BE
  // espone apposta `inspectionOwnerHasL3Module` — il modulo del proprietario
  // dell'ULTIMA attività L3 visibile all'utente su questo ponte — da leggere
  // così com'è, senza ricomporlo in OR con gli altri due campi lato client
  // (altrimenti si riabiliterebbe per sbaglio la creazione). `structure?.l3Access &&`
  // evita il flash del banner prima che la GET del ponte sia arrivata. Vedi
  // apps/api/plans/features/task-18-eredita-diritti-l3-condivisione.md
  // ("Contratto verso i client") e
  // apps/web-bo/plans/features/l3/task-18-l3-condivisione-handoff-fe.md.
  const showL3ModuleExpiredBanner =
    structure?.l3Access && !structure.l3Access.inspectionOwnerHasL3Module;

  const l3ModuleExpiredBanner = showL3ModuleExpiredBanner && (
    <InfoBox
      color="warning.light"
      text="Il modulo L3 non è attivo sul proprietario dell'attività: i dati sono visibili in sola lettura."
    />
  );

  const renderVideo = item => (
    <div>
      <video controls style={{ width: '400px', height: '130px' }}>
        <source src={item.original}></source>
      </video>
    </div>
  );

  useEffect(() => {
    const handleImages = async () => {
      const resultMedia = await get(`bridges/${id}/overviewImages?level=L3`);

      if (resultMedia?.length > 0) {
        const images = [];
        resultMedia?.map(x => {
          images.push({
            original: x.url,
            originalHeight: '130px',
            renderItem: x.isVideo ? renderVideo : null,
          });
        });

        setImagesList(images);
      }
    };

    const handleL3Data = async () => {
      try {
        showLoader();
        const result = await get(
          inspectionId
            ? `bridges/${id}/overview/L3?inspectionId=${inspectionId}`
            : `bridges/${id}/overview/L3`
        );
        result && setOverviewL3(result);
      } catch (err) {
        console.error('Error in get L3 Data Recap: ', err);
      } finally {
        hideLoader();
      }
    };
    setPromises([handleImages(), handleL3Data()]);
  }, []);

  const imagesWeb = [
    { original: graph3, originalHeight: '130px' },
    { original: graph3, originalHeight: '130px' },
    { original: graph3, originalHeight: '130px' },
    { original: graph3, originalHeight: '130px' },
    { original: graph3, originalHeight: '130px' },
    { original: graph3, originalHeight: '130px' },
  ];

  if (
    structure?.status === InspectionStatus.NEW &&
    !overviewL3?.hasInspection
  ) {
    return (
      <>
        <Stack alignItems={'center'}>
          <Typography variant="h3">{`Non sono presenti attività all'interno di questa struttura`}</Typography>
          {userPermissionOnStructure === PermissionType.CanEdit &&
            !overviewL3?.hasInspection && (
              <Button
                onClick={() =>
                  setOpenDrawerNewInspection(!openDrawerNewInspection)
                }
                variant="contained"
                color="primary"
                size="small"
                style={{ marginTop: 8 }}
              >
                Nuova attività
              </Button>
            )}
        </Stack>
        <DrawerNewInspection
          open={openDrawerNewInspection}
          setOpen={setOpenDrawerNewInspection}
          isOwner={isOwner}
          l3Access={structure?.l3Access}
        />
      </>
    );
  }

  // hasInspection true ma hasL3Data false: diagnosis.L3 non è mai stato
  // salvato su nessuna ispezione del ponte (distinto da "nessuna attività",
  // vedi OverviewBuilderL3 — apps/api/.../overview-builder-l3.ts).
  if (overviewL3 && overviewL3.hasL3Data === false) {
    return (
      <Stack alignItems={'center'}>
        <Typography variant="h3">
          {'Nessun dato idraulico (Livello 3) disponibile per questa struttura'}
        </Typography>
      </Stack>
    );
  }

  // Issue #1021, checklist punto 6 — rete di sicurezza: la tab è già
  // disabilitata a monte (getOverviewTab, costants.js) quando
  // hasL3ModuleAccess è falso, ma se ci si arriva comunque (stato di
  // navigazione stale verso questo tab index, o `structure` non ancora
  // caricata al primo render) evita di renderizzare L3RecapContent con
  // overviewL3 rimasto undefined per via del 403 di handleL3Data qui sopra.
  if (structure?.l3Access && !hasL3ModuleAccess(structure.l3Access)) {
    return (
      <Stack alignItems={'center'}>
        <Typography variant="h3">
          {
            "Il riepilogo L3 non è disponibile: nessuno tra te, il proprietario del ponte e il proprietario dell'attività ha il modulo Livello 3 Idraulica attivo. Contattaci per abilitare il modulo e ripristinare l'accesso."
          }
        </Typography>
      </Stack>
    );
  }

  return (
    <>
      {l3ModuleExpiredBanner}
      <L3RecapContent
        values={flattenOverviewL3(overviewL3)}
        imagesList={imagesList}
        imagesFallback={imagesWeb}
        // Issue #1021, punto 16 — stesso pattern L1 (TabL1.jsx/sections/
        // Contesto.jsx): un bottone per sezione, verso l'accordion
        // corrispondente dell'ultima attività L3 valida (`accordion`
        // indefinito per i gruppi eterogenei apre solo la sotto-tab, vedi
        // commento in testa a L3RecapContent.jsx).
        makeApprofondisci={accordion => () =>
          handleDynamicInspectionsTabChange(
            inspectionTabMapping.L3,
            inspectionTabMapping.DatiGenerali,
            accordion,
            navigate,
            STRUCTURES_PATHS.STRUCTURES_LIST,
            id,
            lastL3InspectionId
          )}
        approfondisciDisabled={!canOpenLastL3Inspection}
      />
    </>
  );
};

export { TabL3 };

TabL3.propTypes = {
  structure: PropTypes.object,
  userPermissionOnStructure: PropTypes.oneOf([
    PermissionType.CanView,
    PermissionType.CanEdit,
  ]),
  openDrawerNewInspection: PropTypes.bool,
  setOpenDrawerNewInspection: PropTypes.func,
};
